import { Injectable } from '@angular/core';
import { encode } from 'iconv-lite';
import { OrderSpecialRuleEnum } from '../enums/OrderSpecialRuleEnum';
import { HistOrder } from '../hist/models/hist-order';
import { DateHelper } from '../shared/helpers/date-helper';
import { NHIOrder } from './api-service/NHI/nhiOrder';
import { WholeNHIData } from './api-service/NHI/wholeNHIData';
import { NHIApi } from './api-service/NHI/nhi';
import { WholeHist } from './api-service/hist/whole-hist';
import { HcrService, VisionApiResult } from './hcr-service.service';
import { UserCache } from './user-cache';

@Injectable({
  providedIn: 'root'
})
export class BigTreePharmacyService {
  /**
   * HTTP 實際由 WebhisConsole 發送，因此 127.0.0.1 是指主控台所在的電腦。
   * TODO(部署設定)：若 Go Server 改用其他 IP／Port，應改由系統參數提供，不要散落在呼叫端。
   */
  private readonly defaultApiBaseUrl = 'http://127.0.0.1:18581';

  constructor(
    private hcrService: HcrService,
    private nhiApi: NHIApi
  ) { }

  /**
   * 建立大樹藥局新增病歷所需的 JSON，並透過 WebhisConsole 寫入交換檔。
   */
  public async Send(hist: WholeHist, host: string = '', apiKey: string = ''): Promise<BigTreeMedicalRecordResult> {
    const request = await this.mapRequest(hist);
    const result = await this.hcrService.SendFromUrl(
      `${this.getApiBaseUrl(host)}/api/medical-records`,
      JSON.stringify(request),
      'application/json',
      'utf8',
      this.getHeaders(apiKey)
    );

    return this.parseHttpResult<BigTreeMedicalRecordResult>(result);
  }

  /**
   * 先依 KDT 取得既有 SEQ_NO_C，再透過 WebhisConsole 更新交換檔。
   */
  public async Update(hist: WholeHist, host: string = '', apiKey: string = ''): Promise<BigTreeMedicalRecordResult> {
    const request = await this.mapRequest(hist);
    const apiBaseUrl = this.getApiBaseUrl(host);
    const encodedKdt = encodeURIComponent(request.record.KDT);
    const existingResult = await this.hcrService.GetFromUrl(
      `${apiBaseUrl}/api/medical-records/by-kdt/${encodedKdt}`,
      this.getHeaders(apiKey)
    );
    const existing = this.parseHttpResult<BigTreeMedicalRecordResult>(existingResult);
    const seqNo = Number(existing && existing.record && existing.record.values
      && existing.record.values.SEQ_NO_C);

    if (!Number.isFinite(seqNo) || seqNo <= 0) {
      throw new BigTreePharmacyHttpError('查詢結果缺少有效的 SEQ_NO_C，無法更新大樹藥局病歷。');
    }

    const updateResult = await this.hcrService.PutFromUrl(
      `${apiBaseUrl}/api/medical-records/${encodedKdt}/${seqNo}`,
      JSON.stringify(request),
      'application/json',
      'utf8',
      this.getHeaders(apiKey)
    );

    return this.parseHttpResult<BigTreeMedicalRecordResult>(updateResult);
  }

  /** 使用與衛星藥局相同的主機設定檢查大樹藥局 Go Server。 */
  public TrySend(host: string = '', apiKey: string = ''): Promise<VisionApiResult> {
    return this.hcrService.GetFromUrl(`${this.getApiBaseUrl(host)}/health`, this.getHeaders(apiKey));
  }

  private getApiBaseUrl(host: string): string {
    const value = this.toText(host).trim();
    if (!value) {
      return this.defaultApiBaseUrl;
    }

    return (value.indexOf('http://') === 0 || value.indexOf('https://') === 0
      ? value
      : `http://${value}`).replace(/\/$/, '');
  }

  private getHeaders(apiKey: string): { [key: string]: string } {
    return apiKey ? { 'X-API-Key': apiKey } : {};
  }

  private parseHttpResult<T>(result: VisionApiResult): T {
    if (!result || !result.Successed) {
      throw this.createHttpError(result && result.Returns);
    }

    if (typeof result.Returns !== 'string') {
      return result.Returns as T;
    }

    try {
      return JSON.parse(result.Returns) as T;
    } catch {
      throw new BigTreePharmacyHttpError('大樹藥局 API 回傳的內容不是有效 JSON。', null, null, null, result.Returns);
    }
  }

  private createHttpError(returns: any): BigTreePharmacyHttpError {
    const statusCode = returns && typeof returns === 'object' ? returns.StatusCode : null;
    const responseBody = returns && typeof returns === 'object' && returns.responseBody != null
      ? returns.responseBody
      : returns;
    const errorBody = this.tryParseJson<BigTreeApiErrorBody>(responseBody);
    const message = errorBody && errorBody.error
      ? errorBody.error
      : (typeof responseBody === 'string' && responseBody.trim()
        ? responseBody
        : '無法連線至大樹藥局 API。');

    return new BigTreePharmacyHttpError(
      message,
      statusCode,
      errorBody && errorBody.code,
      errorBody && errorBody.upStatus,
      responseBody
    );
  }

  private tryParseJson<T>(value: any): T {
    if (value == null || value === '') {
      return null;
    }

    if (typeof value === 'object') {
      return value as T;
    }

    try {
      return JSON.parse(value) as T;
    } catch {
      return null;
    }
  }

  private async mapRequest(hist: WholeHist): Promise<BigTreePharmacyRequest> {
    // IC2.0 的 M 欄位與 D15 備註必須使用同一份健保資料，避免
    // regIc、NHIHistory、NHIOrder 來自不同時間點。
    const nhiData = await this.getNhiData(hist);
    return {
      record: this.mapMedicalRecord(hist, nhiData),
      orders: this.mapMedicalOrders(hist, nhiData)
    };
  }

  private async getNhiData(hist: WholeHist): Promise<WholeNHIData> {
    const registerId = hist && hist.Register && hist.Register.Id;
    if (!registerId) {
      return null;
    }

    return await this.nhiApi.GetNHIData(registerId);
  }

  private mapMedicalRecord(hist: WholeHist, nhiData: WholeNHIData): BigTreeMedicalRecord {
    const patient = hist && hist.Patient;
    const register = hist && hist.Register;
    const medicalRecord = hist && hist.Hist;
    const nhiRegIc = nhiData && nhiData.RegIc;
    const nhiHist = nhiData && nhiData.Hist;
    const regDate = this.toValidDate(register && register.RegDate);
    const patientNo = this.toText(patient && patient.PatientNo);
    const phoneArea = this.toText(patient && patient.PhoneArea);
    const phone = this.toText(patient && patient.Phone);
    const roomCode = this.toText(register && register.RoomCode);

    return {
      KDT: patientNo.slice(-7) + this.formatRocDateTime(regDate),
      HOSPCODE: this.toText(UserCache.getLoginUser() && UserCache.getLoginUser().Clinic
        && UserCache.getLoginUser().Clinic.NHICode),
      PRS_ID: this.toText(patient && patient.CId),
      NAME: this.toText(patient && patient.CName),

      // TODO(規格確認)：目前 WholeHist.Patient.Sex 沿用既有數值，暫定 0=女、1=男。
      SEX: patient && patient.Sex != null ? String(patient.Sex) : '',

      BIRTHDAY: this.formatGregorianDate(this.toValidDate(patient && patient.Birthday)),

      // TODO(臨時規則)：目前未轉換 Patient.Country，一律以「1=本國籍」傳送。
      NATIONALIT: '1',

      // TODO(規格確認)：區碼與電話目前直接相接，不額外加入「-」或空白。
      TEL1: phoneArea + phone,
      CELL: this.toText(patient && patient.CellPhone),
      ZIP: this.toText(patient && patient.ZipCode),
      ADDRESS: patient.Street,
      EXAM_DATE: this.formatGregorianDate(regDate),
      ID_TYPE: this.mapIdType(register && register.ICode),
      DR_CODE: this.toText(register && register.DoctorCId),
      DEPT: this.toText(register && register.MedDeptCode),
      SICK_CALL: register && register.TimeSec != null ? String(register.TimeSec) : '',
      OFFICE: roomCode.slice(-1),
      ICD_CODE1: this.toText(medicalRecord && medicalRecord.Dx1),
      ICD_CODE2: this.toText(medicalRecord && medicalRecord.Dx2),
      ICD_CODE3: this.toText(medicalRecord && medicalRecord.Dx3),
      ICD_CODE4: this.toText(medicalRecord && medicalRecord.Dx4),
      ICD_CODE5: this.toText(medicalRecord && medicalRecord.Dx5),
      ICD_CODE6: this.toText(medicalRecord && medicalRecord.Dx6),
      CURE_CODE1: this.toText(medicalRecord && medicalRecord.SPTX1),
      CURE_CODE2: this.toText(medicalRecord && medicalRecord.SPTX2),
      CURE_CODE3: this.toText(medicalRecord && medicalRecord.SPTX3),
      CURE_CODE4: this.toText(medicalRecord && medicalRecord.SPTX4),
      CASETYPE: this.toText(medicalRecord && medicalRecord.Case),
      DRUG_SEQ: this.toText(medicalRecord && medicalRecord.CntPresNo),
      DRUG_NO: this.toText(medicalRecord && medicalRecord.CntPresTot),
      MEDDAYS: this.toNumber(medicalRecord && medicalRecord.Days),
      CARD_NO: this.toText(register && register.Icno),
      NOCARD_FLA: register && register.IType === '3' ? 'Y' : 'N',
      STOCK_NO: '',

      EXAM_TID: this.toText(nhiRegIc && nhiRegIc.Itreatnum),
      DRUGKIND: this.toText(nhiHist && nhiHist.Idru),
      NEWBORNDAT: this.formatNhiGregorianDate(nhiRegIc && nhiRegIc.BabyDate),
      NEWBORNMAR: this.toText(nhiRegIc && nhiRegIc.BirthsNum),
      NEWBORNTRE: this.toText(nhiRegIc && nhiRegIc.BabyMark),
      CARDREPAIR: this.toText(nhiRegIc && nhiRegIc.IreCard),
      CDPDAYS: this.toNumber(nhiHist && nhiHist.IChrContDays),
      RLPDAYS: this.toNumber(nhiHist && nhiHist.ICtrlDays),
      GENNO: this.toText(nhiHist && nhiHist.IGenFreq) || '0',
      CCDNO: this.toText(nhiHist && nhiHist.IChrFreq) || '0',
      CDPNO: this.toText(nhiHist && nhiHist.IChrContFreq) || '0',
      RLGNO: this.toText(nhiHist && nhiHist.ICtrlFreq) || '0',
      RLCNO: this.toText(nhiHist && nhiHist.ICtrlChrFreq) || '0',
      RLPNO: this.toText(nhiHist && nhiHist.ICtrlContFreq) || '0',
      PAY_TYPE: this.toText(nhiHist && nhiHist.ICase),

      // TODO(規格確認)：交換格式提到依醫令註記 1/2/3 設定，但目前尚無明確來源及轉換規則，暫留空白。
      MED_IN_ONE: '',

      MEMO1: '',
      MEMO2: '',
      MEMO3: '',
      MEMO4: '',
      MEMO5: '',
      OP_USER: '',
      UP_USER: ''
    };
  }

  private mapMedicalOrders(hist: WholeHist, nhiData: WholeNHIData): BigTreeMedicalOrder[] {
    const orders = hist && hist.Hist && hist.Hist.HRxs ? hist.Hist.HRxs : [];
    const nhiHist = nhiData && nhiData.Hist;
    const nhiOrders = (nhiData && nhiData.Orders ? nhiData.Orders : [])
      .slice()
      .sort((left, right) => this.toNumber(left && left.Iseq) - this.toNumber(right && right.Iseq));
    const usedNhiOrderIndexes = new Set<number>();

    return orders.map((order, index) => {
      const nhiOrderIndex = this.findNhiOrderIndex(order, nhiOrders, usedNhiOrderIndexes);
      const nhiOrder = nhiOrderIndex >= 0 ? nhiOrders[nhiOrderIndex] : null;
      if (nhiOrderIndex >= 0) {
        usedNhiOrderIndexes.add(nhiOrderIndex);
      }

      return {
        // KDT、SEQ_NO_C 由 Go API 依母檔資料覆寫，Angular 不傳入。
        ORD_ITEM: index + 1,
        ORD_ID: this.toText(order && order.RxCode),
        IS_PAY: this.mapIsPay(order && order.SPRule),
        USE_QTY: this.mapUseQuantity(order),
        FREQUENCY: this.toText(order && order.Freq),
        METHOD: this.toText(order && order.Way),
        USE_DAY: this.getUseDay(order),
        TOTAL_QTY: this.toNumber(order && order.TotalDose),
        IS_GRIND: this.toText(order && order.Remark).indexOf('磨粉') >= 0 ? '1' : '0',
        DRUGKIND: this.toText(nhiHist && nhiHist.Idru),
        ORDCMD: this.truncateBig5(this.toText(nhiOrder && nhiOrder.IRemark), 100)
      };
    });
  }

  private findNhiOrderIndex(
    order: HistOrder,
    nhiOrders: NHIOrder[],
    usedNhiOrderIndexes: Set<number>
  ): number {
    const stdCode = this.toText(order && order.StdCode).trim();
    const frequency = this.toText(order && order.Freq).trim();
    const useDay = this.getUseDay(order);
    const totalDose = this.toNumber(order && order.TotalDose);

    return nhiOrders.findIndex((nhiOrder, index) =>
      !usedNhiOrderIndexes.has(index)
      && this.toText(nhiOrder && nhiOrder.NhiDrugCode).trim() === stdCode
      && this.toText(nhiOrder && nhiOrder.Frequency).trim() === frequency
      && this.toNumber(nhiOrder && nhiOrder.Days) === useDay
      && this.toNumber(nhiOrder && nhiOrder.TotalDose) === totalDose
    );
  }

  private getUseDay(order: HistOrder): number {
    return this.toNumber(order && (order.Days === 0 ? order.InfoDays : order.Days));
  }

  private mapIdType(iCodeValue: string): string {
    const iCode = this.toText(iCodeValue).trim().toUpperCase();
    if (iCode === '000' || iCode === 'A') {
      return '-1';
    }

    // TODO(規格確認)：非自費、一般健保時，暫依需求直接擷取 ICode 末三碼作為重大傷病代碼。
    return iCode.slice(-3);
  }

  private mapIsPay(specialRule: number): string {
    switch (specialRule) {
      case OrderSpecialRuleEnum.A2_SelfPayment:
        // 自費
        return '1';
      case OrderSpecialRuleEnum.A3_FreeAndPrint:
      case OrderSpecialRuleEnum.A4_FreeNotPrint:
        // 贈送
        return '2';
      default:
        // 健保
        return '0';
    }
  }

  private mapUseQuantity(order: HistOrder): number {
    if (!order) {
      return 0;
    }

    switch (order.QtyRule) {
      case HistOrder.QtyRule_Dose:
        return this.toNumber(order.Dose);
      case HistOrder.QtyRule_DailyDose:
        return this.toNumber(order.DailyDose);
      case HistOrder.QtyRule_TotalDose:
        return this.toNumber(order.TotalDose);
      default:
        // TODO(規格確認)：未辨識的 QtyRule 暫以畫面輸入量 QTY 作為 USE_QTY。
        return this.toNumber(order.QTY);
    }
  }

  private formatRocDateTime(date: Date): string {
    if (!date) {
      return '';
    }

    // KDT 的日期及時間一律取 Register.RegDate，該值與健保刷卡時間相依，不得改取目前系統時間。
    return DateHelper.getROCFullDateString(date) + DateHelper.getTimeString(date, '', true);
  }

  private formatGregorianDate(date: Date): string {
    return date ? DateHelper.getFullDateString(date) : '';
  }

  private formatNhiGregorianDate(value: string): string {
    const text = this.toText(value).trim();
    if (!text) {
      return '';
    }

    const date = text.length === 7
      ? DateHelper.parseROCDate(text)
      : this.toValidDate(text);
    return date && DateHelper.isValidDate(date) ? DateHelper.getFullDateString(date) : '';
  }

  private truncateBig5(value: string, maxBytes: number): string {
    if (!value) {
      return '';
    }

    const text = this.toText(value);
    if (encode(text, 'big5').length <= maxBytes) {
      return text;
    }

    let result = '';
    let usedBytes = 0;
    for (const character of text) {
      const characterBytes = encode(character, 'big5').length;
      if (usedBytes + characterBytes > maxBytes) {
        break;
      }
      result += character;
      usedBytes += characterBytes;
    }
    return result;
  }

  private toValidDate(value: Date | string): Date {
    if (!value) {
      return null;
    }

    const date = new Date(value);
    return DateHelper.isValidDate(date) ? date : null;
  }

  private toText(value: unknown): string {
    return value == null ? '' : String(value);
  }

  private toNumber(value: unknown): number {
    const numberValue = Number(value);
    return Number.isFinite(numberValue) ? numberValue : 0;
  }
}

/** 大樹藥局新增／更新病歷的 Request JSON。 */
export interface BigTreePharmacyRequest {
  /** 病歷母檔 OPBM。 */
  record: BigTreeMedicalRecord;
  /** 醫令子檔 OPBD。 */
  orders: BigTreeMedicalOrder[];
}

/** OPBM 病歷母檔交換欄位。 */
export interface BigTreeMedicalRecord {
  /** 交換檔唯一值：病歷號末 7 碼 + RegDate 民國日期 7 碼 + RegDate 時間 6 碼。 */
  KDT: string;
  /** 藥歷總筆數；由 Go API 新增時產生，重傳異動時不得更改。 */
  SEQ_NO_C?: number;
  /** 來源院所健保代碼。 */
  HOSPCODE: string;
  /** 病人身分證號。 */
  PRS_ID: string;
  /** 病人姓名。 */
  NAME: string;
  /** 性別：暫定 0=女、1=男，需再與大樹端確認代碼定義。 */
  SEX: string;
  /** 出生日期，西元 yyyyMMdd。 */
  BIRTHDAY: string;
  /** 國籍：1=本國籍、2=外國籍；目前臨時固定傳 1。 */
  NATIONALIT: string;
  /** 市內電話；目前由 PhoneArea + Phone 直接相接。 */
  TEL1: string;
  /** 行動電話。 */
  CELL: string;
  /** 郵遞區號。 */
  ZIP: string;
  /** 地址；目前由 City + Area + Street 直接相接。 */
  ADDRESS: string;
  /** 看診日期，取 Register.RegDate，西元 yyyyMMdd。 */
  EXAM_DATE: string;
  /** 身分類別：ICode=000/A 時為 -1，其餘取 ICode 末三碼。 */
  ID_TYPE: string;
  /** 醫師身分證號。 */
  DR_CODE: string;
  /** 科別代碼。 */
  DEPT: string;
  /** 時段別：1=早、2=午、3=晚、4=其他。 */
  SICK_CALL: string;
  /** 診間別，取 RoomCode 末一碼。 */
  OFFICE: string;
  /** 國際疾病分類代碼 1。 */
  ICD_CODE1: string;
  /** 國際疾病分類代碼 2。 */
  ICD_CODE2: string;
  /** 國際疾病分類代碼 3。 */
  ICD_CODE3: string;
  /** 國際疾病分類代碼 4。 */
  ICD_CODE4: string;
  /** 國際疾病分類代碼 5。 */
  ICD_CODE5: string;
  /** 國際疾病分類代碼 6。 */
  ICD_CODE6: string;
  /** 治療代碼 1。 */
  CURE_CODE1: string;
  /** 治療代碼 2。 */
  CURE_CODE2: string;
  /** 治療代碼 3。 */
  CURE_CODE3: string;
  /** 治療代碼 4。 */
  CURE_CODE4: string;
  /** 案件分類。 */
  CASETYPE: string;
  /** 慢性病連續處方箋調劑次數。 */
  DRUG_SEQ: string;
  /** 慢性病連續處方箋總調劑次數。 */
  DRUG_NO: string;
  /** 給藥日份。 */
  MEDDAYS: number;
  /** 健保卡就醫序號。 */
  CARD_NO: string;
  /** 欠卡註記：Y=欠卡、N=有卡；目前以 IType=3 判斷欠卡。 */
  NOCARD_FLA: string;
  /** 領藥號；由 Go API 新增時產生，重傳異動時不得更改。 */
  MEDICINE_N?: number;
  /** 上傳狀態；由 Go API 控管寫檔流程。 */
  UP_STATUS?: string;
  /** 庫存編號，預設空白。 */
  STOCK_NO: string;
  /** 診所端就醫識別碼，來源為 IC2 M15。 */
  EXAM_TID: string;
  /** 處方箋調劑方式，原值取 NHIHistory.Idru。 */
  DRUGKIND: string;
  /** 新生兒出生日期，西元 yyyyMMdd。 */
  NEWBORNDAT: string;
  /** 新生兒胞胎註記，來源為 IC2 M09。 */
  NEWBORNMAR: string;
  /** 新生兒就醫註記，來源為 IC2 M10。 */
  NEWBORNTRE: string;
  /** 補卡註記，來源為 IC2 M12。 */
  CARDREPAIR: string;
  /** 慢箋總給藥天數，來源為 IC2 M21。 */
  CDPDAYS: number;
  /** 管制慢連箋總給藥天數，來源為 IC2 M22。 */
  RLPDAYS: number;
  /** 一般箋可調次數，來源為 IC2 M24。 */
  GENNO: string;
  /** 慢箋可調次數，來源為 IC2 M25。 */
  CCDNO: string;
  /** 慢連箋可調次數，來源為 IC2 M26。 */
  CDPNO: string;
  /** 管一般可調次數，來源為 IC2 M27。 */
  RLGNO: string;
  /** 管慢箋可調次數，來源為 IC2 M28。 */
  RLCNO: string;
  /** 管慢連箋可調次數，來源為 IC2 M29。 */
  RLPNO: string;
  /** 給付代碼，來源為 IC2 M51。 */
  PAY_TYPE: string;
  /** 領藥註記；來源與 1/2/3 轉換規則尚待確認，目前空白。 */
  MED_IN_ONE: string;
  /** 備註 1，預設空白。 */
  MEMO1: string;
  /** 備註 2，預設空白。 */
  MEMO2: string;
  /** 備註 3，預設空白。 */
  MEMO3: string;
  /** 備註 4，預設空白。 */
  MEMO4: string;
  /** 備註 5，預設空白。 */
  MEMO5: string;
  /** 建立人員，預設空白。 */
  OP_USER: string;
  /** 建立日；由 Go API 新增時寫入系統日期 yyyyMMdd。 */
  OP_DATE?: string;
  /** 更新人員，預設空白。 */
  UP_USER: string;
  /** 更新日；由 Go API 更新時寫入系統日期 yyyyMMdd。 */
  UP_DATE?: string;
}

/** OPBD 醫令子檔交換欄位。 */
export interface BigTreeMedicalOrder {
  /** 交換檔唯一值；由 Go API 使用母檔 KDT 覆寫。 */
  KDT?: string;
  /** 藥歷總筆數；由 Go API 使用母檔 SEQ_NO_C 覆寫。 */
  SEQ_NO_C?: number;
  /** 醫令項次，從 1 開始。 */
  ORD_ITEM: number;
  /** 醫令代碼，來源為 HRx.RxCode。 */
  ORD_ID: string;
  /** 給付別：0=健保、1=自費、2=贈送。 */
  IS_PAY: string;
  /** 單次／每日／總量輸入值，依 QtyRule 選擇 Dose、DailyDose 或 TotalDose。 */
  USE_QTY: number;
  /** 使用頻率。 */
  FREQUENCY: string;
  /** 使用途徑。 */
  METHOD: string;
  /** 使用日數；Days=0 時改取 InfoDays。 */
  USE_DAY: number;
  /** 醫令總量。 */
  TOTAL_QTY: number;
  /** 是否磨粉：1=磨粉、0=非磨粉；目前以 Remark 是否包含「磨粉」判斷。 */
  IS_GRIND: string;
  /** 處方箋調劑方式，原值取 NHIHistory.Idru。 */
  DRUGKIND: string;
  /** IC2.0 D15 醫令備註，依 Big5 bytes 截斷至 100 bytes。 */
  ORDCMD: string;
}

/** Go API 回傳的一筆 DBF Record。 */
export interface BigTreeDbfRecord<TValues> {
  /** DBF 實體資料列編號。 */
  recordNumber: number;
  /** 是否已被標記刪除。 */
  deleted: boolean;
  /** DBF 欄位內容。 */
  values: TValues;
}

/** Go API 新增、查詢或更新病歷的回傳內容。 */
export interface BigTreeMedicalRecordResult {
  /** OPBM 病歷母檔。 */
  record: BigTreeDbfRecord<BigTreeMedicalRecord>;
  /** OPBD 醫令子檔。 */
  orders: BigTreeDbfRecord<BigTreeMedicalOrder>[];
}

/** Go API 非 2xx 回應內容。 */
export interface BigTreeApiErrorBody {
  /** 錯誤訊息。 */
  error?: string;
  /** 可供程式判斷的錯誤代碼，例如 RECORD_ALREADY_PROCESSING。 */
  code?: string;
  /** 對方系統處理狀態；值為 2 時禁止更新。 */
  upStatus?: string;
}

/**
 * 大樹藥局 HTTP 呼叫錯誤。
 * 保留 WebhisConsole 收到的 HTTP 狀態與 Go API 錯誤欄位，供畫面判斷 400／401／404／409。
 */
export class BigTreePharmacyHttpError extends Error {
  constructor(
    message: string,
    public statusCode: number | string = null,
    public code: string = null,
    public upStatus: string = null,
    public responseBody: any = null
  ) {
    super(message);
    this.name = 'BigTreePharmacyHttpError';
    Object.setPrototypeOf(this, BigTreePharmacyHttpError.prototype);
  }
}
